import { useState, useEffect, useCallback, useContext } from "react";
import { Modal, Button, Spinner, Form, Row } from "react-bootstrap";
import {
    APIEditVisLandmarks,
    APIEditLandmarksWithFiles,
    APITranslate,
} from "../../helper/APIFunctions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPencil,
    faArrowUp,
    faArrowDown,
    faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import "./LandmarkModal.css";
import DeleteModal from "./Delete";
import { setSelectionRange } from "@testing-library/user-event/dist/utils";
import { UserInfo } from "../../helper/Context";
import { CLOUD_TRANSLATE_LANGUAGE_MAP, ELEVENLABS_LANGUAGE_MAP, ELEVENLABS_LANGUAGES } from "../../helper/Language_Utils";
import VoiceSelector from "../VoiceSelector";

const AUDIOFILESIZEMB = 4;

const SubmitChanges = ({ onStart = () => { }, onSuccess = () => { }, onError = () => { }, audioFileLandmarks, removeAudioLandmarks, currentLandmarks, language, selectedVoice = null, visualization = null, viewerId = null }) => {
    let APIFunc = Object.values(audioFileLandmarks).length > 0 ? APIEditLandmarksWithFiles : APIEditVisLandmarks;
    if (selectedVoice) {
        APIFunc = (vis, newLandmarks, successCBInit, failureCBInit, isOrg, args) => APIEditLandmarksWithFiles(vis, newLandmarks, successCBInit, failureCBInit, isOrg, {voice_id: selectedVoice, ...args});
    }
    const getLabelIndex = (landmark) => {
        const ind = currentLandmarks.findIndex(l => l.name === landmark.name);
        return ind >= 0 ? ind + 1 : 999;
    };
    onStart();
    const submitLandmarks = (landmarks, translated = false) => {
        landmarks.sort((a, b) => getLabelIndex(a) - getLabelIndex(b));
        for (const [ind, landmark] of landmarks.entries()) {
            landmark.description = landmark.properties.description;
            landmark.title = landmark.properties.title;
            if (removeAudioLandmarks[landmark.name]) {
                landmark.audio = null;
                landmark.audioDuration = null;
            }
            else if (audioFileLandmarks[landmark.name]) {
                landmark.audioFile = audioFileLandmarks[landmark.name];
            }
            landmark.properties.labelText = getLabelIndex(landmark).toString();
            if (landmark.properties.labelText === "999") {
                delete landmark.properties.labelText;
            }
        }
        if (language && !translated) {
            const texts = [];
            for (const landmark of landmarks) {
                texts.push(landmark.properties.title);
                texts.push(landmark.properties.description);
            }
            APITranslate(texts, language, (res) => {
                for (const [ind, landmark] of landmarks.entries()) {
                    landmark.title = res[2 * ind].translation;
                    landmark.description = res[2 * ind + 1].translation;
                    landmark.properties.title = landmark.title;
                    landmark.properties.description = landmark.description;
                }
                submitLandmarks(landmarks, true);
                return;
            }, (err) => {
                onError(err.message || (err.toUpperCase ? err : "Internal Server Error"));
                return;
            });
            return;
        }
        APIFunc(visualization, landmarks, (res) => {
            onSuccess(res);
            return

        }, (err) => {
            onError(err.message || (err.toUpperCase ? err : "Internal Server Error"));
            return;
        });
    };
    const handler = (event) => {
        if (event.data.description === "CurrentView" && !event.data.error && event.data.id == "FinalizeLandmarks") {
            const landmarks = [...event.data.result.landmarks];
            submitLandmarks(landmarks);
        }
        else if (event.data.description === "CurrentLandmarks" && event.data.error) {
            onError("Error getting current landmarks");
            return;
        }
        window.removeEventListener("message", handler);
    };
    window.addEventListener("message", handler);
    sendMessageToViewer(viewerId, {
        description: "GetCurrentView",
        id: "FinalizeLandmarks",
    });
    return;
}

// Helper function to send messages to the viewer
const sendMessageToViewer = (viewerId, message) => {
    const element = document.getElementById(viewerId);
    if (element && element.contentWindow) {
        element.contentWindow.postMessage(message, "https://vis.snorkle.io");
    }
};

// Helper function to wait for a specific message response
const waitForMessage = (messageType, timeout = 5000) => {
    return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            window.removeEventListener("message", messageHandler);
            reject(new Error(`Timeout waiting for ${messageType}`));
        }, timeout);

        const messageHandler = (event) => {
            const data = event.data;
            if (data.description === messageType) {
                clearTimeout(timeoutId);
                window.removeEventListener("message", messageHandler);
                resolve(data);
            }
        };

        window.addEventListener("message", messageHandler);
    });
};

const SubModal = (props) => {
    const lm = props.landmark || {};
    const [title, setTitle] = useState((lm.properties || lm).title || "");
    const [desc, setDesc] = useState((lm.properties || lm).description || "");
    const [markerPos, setMarkerPos] = useState((lm.properties || lm).position || null);
    const [cameraPos, setCameraPos] = useState((lm.properties || lm).cameraPos || null);
    const [duration, setDuration] = useState((lm.properties || lm).tourDuration || 10);
    const [upVector, setUpVector] = useState((lm.properties || lm).upVector || [0, 1, 0]);
    const [durationRaw, setDurationRaw] = useState(
        (lm.duration || 10).toString()
    );
    const [durationError, setDurationError] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [removeAudio, setRemoveAudio] = useState(false);
    const [touched, setTouched] = useState(false);
    const [isCreatingLandmark, setIsCreatingLandmark] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [component, setComponent] = useState((lm.properties || lm).parent || null);
    const [cameraLookAt, setCameraLookAt] = useState((lm?.properties?.cameraLookAt || lm?.cameraLookAt) || null);
    const [shape, setShape] = useState((lm.properties || lm).shape || "circle");
    const [size, setSize] = useState((lm.properties || lm).markerScale || 1.0);
    const [sizeRaw, setSizeRaw] = useState(size.toString());
    const [sizeError, setSizeError] = useState(null);
    const [depthOffset, setDepthOffset] = useState((lm.properties || lm).depthOffset || 2.5);
    const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
    const [depthOffsetError, setDepthOffsetError] = useState(null);
    const [depthOffsetRaw, setDepthOffsetRaw] = useState(depthOffset.toString());
    const [componentPath, setComponentPath] = useState((lm.properties || lm).parentPath || null);
    const onMsgListener = useCallback((event) => {
        const data = event.data;

        // Log ALL messages to see what cancel sends

        if (data.description === "NewLandmarkPosition") {
            if (data.result === "working") {
                // Still processing
                return;
            }
            if (!data.result || data.result === "cancelled") {
                // User cancelled landmark creation - just show the modal again without changes
                props.showFcn();
                setIsCreatingLandmark(false);
            } else {
                // User completed landmark positioning
                setMarkerPos(Array.from(data.result.position));
                setCameraPos(Array.from(data.result.cameraPos));
                setUpVector(Array.from(data.result.cameraUp) || [0, 1, 0]);
                setComponent(data.result.component);
                setComponentPath(data.result?.componentPath ?? null);
                setCameraLookAt(Array.from(data.result.cameraLookAt));
                props.showFcn();
                setIsCreatingLandmark(false);
                setTouched(true);
            }
            return;
        }

        if (data.description === "LandmarksAdded") {
            if (data.result === "working") {
                // Still processing
                setIsSaving(true);
                return;
            } else if (data.result === "done") {
                // Landmark creation completed
                setIsCreatingLandmark(false);
                setIsSaving(false);
                props.showFcn();
                // Get the updated landmarks to find the new one
                sendMessageToViewer(props.viewerId, "GetLandmarks");
            }
            return;
        }

        if (data.description === "ComponentPropertiesUpdate") {
            if (data.result === "working") {
                // Still processing
                setIsSaving(true);
                return;
            } else if (data.result === "done") {
                // Landmark update completed
                setIsSaving(false);
                // Close the submodal and refresh landmarks
                props.onHide();
                sendMessageToViewer(props.viewerId, "GetLandmarks");
            }
            return;
        }

        if (data.description === "CurrentLandmarks") {
            // Find the newly created landmark and finalize it
            const newLandmark = data.result.find(l => l.title === title && l.description === desc);
            if (newLandmark) {
                let temp = {
                    title: title,
                    description: desc,
                    position: newLandmark.position,
                    name: newLandmark.name,
                };
                if (!props.noCameraPos) {
                    temp.duration = duration;
                    temp.audio = audioFile ? "pending" : (removeAudio ? null : lm.audio);
                    temp.audioDuration = removeAudio ? null : lm.audioDuration;
                    temp.cameraPos = newLandmark.cameraPos || cameraPos;
                    temp.upVector = newLandmark.upVector || upVector;
                }
                if (newLandmark.component) {
                    temp.component = newLandmark.component;
                }
                if (newLandmark.componentPath) {
                    temp.componentPath = newLandmark.componentPath;
                }
                props.onFinalize(props.index, temp);
            }
            return;
        }
    }, [title, desc, duration, audioFile, removeAudio, lm.audio, lm.audioDuration, cameraPos, upVector, props]);

    useEffect(() => {
        window.addEventListener("message", onMsgListener);
        return () => {
            window.removeEventListener("message", onMsgListener);
        };
    }, [onMsgListener]);

    useEffect(() => {
        if (/^\s*\d*(\.\d*)?\s*$/.test(durationRaw)) {
            const duration = parseFloat(durationRaw);
            if (!duration) {
                setDurationError("Duration must not be zero");
                return;
            }
            setDuration(duration);
            setDurationError(null);
            return;
        }
        setDurationError("Invalid Format");
    }, [durationRaw]);

    useEffect(() => {
        if (/^\s*\d*(\.\d*)?\s*$/.test(sizeRaw)) {
            const size = parseFloat(sizeRaw);
            if (!size || size <= 0) {
                setSizeError("Size must be positive");
                return;
            }
            setSize(size);
            setSizeError(null);
            return;
        }
        setSizeError("Invalid Format");
    }, [sizeRaw]);

    useEffect(() => {
        if (/^\s*\d*(\.\d*)?\s*$/.test(depthOffsetRaw)) {
            const depthOffset = parseFloat(depthOffsetRaw);
            if (!depthOffset || depthOffset <= 0) {
                setDepthOffsetError("Depth offset must be positive");
                return;
            }
            setDepthOffset(depthOffset);
            setDepthOffsetError(null);
            return;
        }
        setDepthOffsetError("Invalid Format");
    }, [depthOffsetRaw]);

    const handleCreateLandmark = async () => {
        if (!title || !title.match(/\S/)) {
            return;
        }

        setIsCreatingLandmark(true);

        // Hide the submodal to show the viewer for landmark positioning
        props.hideFcn();

        // Send message to prompt viewer for landmark and camera positioning
        sendMessageToViewer(props.viewerId, {
            description: "CreateNewLandmark"
        });

    };

    const handleCancelPositioning = () => {
        setIsCreatingLandmark(false);
        props.showFcn();
        // Try to tell the viewer to cancel
        sendMessageToViewer(props.viewerId, {
            description: "CancelLandmarkCreation"
        });
    };

    return (
        <Modal show={props.show} onHide={props.onHide} dialogClassName="landmark-submodal">
            <Modal.Header closeButton closeVariant="white">
                <Modal.Title>{props.index === -1 ? "New Landmark" : "Edit Landmark"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group controlId={"landmark-title"} className="mb-3">
                    <Row>
                        <Form.Label className="col-sm-3">Title</Form.Label>
                        <Form.Control
                            type="text"
                            value={title}
                            placeholder="Landmark Title"
                            onChange={(event) => {
                                setTitle(event.target.value);
                                setTouched(true);
                            }}
                            dir="auto"
                            isInvalid={!title || !title.match(/\S/)}
                        />
                        <Form.Control.Feedback type="invalid">
                            Title cannot be empty
                        </Form.Control.Feedback>
                    </Row>
                </Form.Group>
                <Form.Group controlId={"landmark-description"} className="mb-3">
                    <Row>
                        <Form.Label className="col-sm-3">Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            value={desc}
                            dir="auto"
                            placeholder="Optional Description for Landmark"
                            onChange={(event) => {
                                setDesc(event.target.value);
                                setTouched(true);
                            }}
                        />
                    </Row>
                </Form.Group>
                {props.canAudio ? (
                    <Form.Group controlId={"landmark-duration"} className="mb-3">
                        <Form.Label>Landmark Duration (seconds)</Form.Label>
                        <Form.Control
                            value={durationRaw}
                            onChange={(event) => {
                                setDurationRaw(event.target.value);
                                setTouched(true);
                            }}
                            isInvalid={durationError}
                            disabled={(lm.audio || lm.information?.audio) && !removeAudio}
                        />
                        <Form.Control.Feedback type="invalid">
                            {durationError}
                        </Form.Control.Feedback>
                        {(lm.audio || lm.information?.audio) && !removeAudio && (
                            <Form.Text className="text-muted">
                                Duration is controlled by the audio file
                            </Form.Text>
                        )}
                    </Form.Group>
                ) : null}
                <Form.Group controlId={"landmark-pos"} className="mb-3">
                    <Button
                        className="set-position-btn"
                        onClick={handleCreateLandmark}
                        disabled={!title || !title.match(/\S/) || isCreatingLandmark}
                    >
                        {isCreatingLandmark ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Positioning Landmark...
                            </>
                        ) : (
                            markerPos && (cameraPos || props.noCameraPos) ? "Edit Landmark Position" : "Set Landmark Position"
                        )}
                    </Button>
                </Form.Group>
                <Form.Group controlId={"landmark-size"} className="mb-3">
                    <Form.Label>Size</Form.Label>
                    <Form.Control
                        value={sizeRaw}
                        onChange={(event) => {
                            setSizeRaw(event.target.value);
                            setTouched(true);
                        }}
                        isInvalid={sizeError}
                    />
                    <Form.Control.Feedback type="invalid">
                        {sizeError}
                    </Form.Control.Feedback>
                </Form.Group>
                {props.canAudio ? (
                    <>
                        {(lm.audio || lm.information?.audio) ? (
                            <Form.Group className="mb-3">
                                <Form.Check
                                    label="Remove Current Audio Track"
                                    checked={removeAudio}
                                    onChange={(e) => {
                                        setRemoveAudio(e.target.checked);
                                        setTouched(true);
                                    }}
                                />
                            </Form.Group>
                        ) : null}
                        {(!lm.audio && !lm.information?.audio) || removeAudio ? (
                            <Form.Group className="mb-3">
                                <Form.Label className="col-auto">Audio Track</Form.Label>
                                <Form.Control
                                    type="file"
                                    onChange={(event) => {
                                        setAudioFile(
                                            event.target.files.length ? event.target.files[0] : null
                                        );
                                        setTouched(true);
                                    }}
                                    isInvalid={
                                        audioFile && audioFile.size > AUDIOFILESIZEMB * 1024 * 1024
                                    }
                                    accept="audio/mpeg"
                                />
                                <Form.Control.Feedback type="invalid">
                                    Selected file is too large.
                                </Form.Control.Feedback>
                                <Form.Text>
                                    Accepts a .mp3 file with a limit of {AUDIOFILESIZEMB}MB
                                </Form.Text>
                            </Form.Group>
                        ) : null}
                    </>
                ) : null}
                <Button
                    className="save-landmark-btn"
                    onClick={async () => {
                        if (props.index === -1) {
                            // Creating a new landmark
                            const landmarkName = `landmark_${Math.random().toString(36).substr(2, 9)}`;

                            const landmarkSpec = {
                                title: title,
                                description: desc,
                                position: markerPos,
                                name: landmarkName,
                                shape: shape,
                                labelText: props.numLandmarks + 1,
                                depthOffset: depthOffset,
                                markerScale: size
                            };

                            if (!props.noCameraPos) {
                                landmarkSpec.cameraPos = cameraPos;
                                landmarkSpec.upVector = upVector;
                                landmarkSpec.tourDuration = duration;
                                landmarkSpec.cameraLookAt = cameraLookAt;
                                landmarkSpec.parent = component;
                                landmarkSpec.parentPath = componentPath;
                            }

                            // Send AddLandmarks message to create the landmark
                            sendMessageToViewer(props.viewerId, {
                                description: "AddLandmarks",
                                result: [landmarkSpec]
                            });

                            // Prepare data for parent component
                            let t = {
                                title: title,
                                description: desc,
                                position: markerPos,
                                name: landmarkName,
                            };
                            if (!props.noCameraPos) {
                                t.duration = duration;
                                t.audioFile = audioFile;
                                t.audio = removeAudio ? null : lm.audio;
                                t.audioDuration = removeAudio ? null : lm.audioDuration;
                                t.cameraPos = cameraPos;
                                t.upVector = upVector;
                                t.cameraLookAt = cameraLookAt;
                                t.depthOffset = depthOffset;
                                t.shape = shape;
                                t.markerScale = size;
                            }
                            props.onFinalize(props.index, t);
                        } else {
                            // Updating an existing landmark
                            const updateProperties = {
                                title: title,
                                description: desc,
                                position: markerPos,
                            };

                            if (!props.noCameraPos) {
                                updateProperties.cameraPos = cameraPos;
                                updateProperties.upVector = upVector;
                                updateProperties.tourDuration = duration;
                                updateProperties.cameraLookAt = cameraLookAt;
                            }
                            updateProperties.shape = shape;
                            updateProperties.depthOffset = depthOffset;
                            updateProperties.markerScale = size;
                            // Send SetComponentProperties message to update the existing landmark
                            sendMessageToViewer(props.viewerId, {
                                description: "SetComponentProperties",
                                target: { name: lm.name },
                                result: updateProperties
                            });

                            // Prepare data for parent component
                            let t = {
                                title: title,
                                description: desc,
                                position: markerPos,
                                name: lm.name,
                                shape: shape,
                                depthOffset: depthOffset,
                                markerScale: size,
                            };
                            if (!props.noCameraPos) {
                                t.duration = duration;
                                t.audioFile = audioFile;
                                t.audio = removeAudio ? null : lm.audio;
                                t.audioDuration = removeAudio ? null : lm.audioDuration;
                                t.removeAudio = removeAudio;
                                t.cameraPos = cameraPos;
                                t.upVector = upVector;
                                t.cameraLookAt = cameraLookAt;
                                t.shape = shape;
                                t.markerScale = size;
                            }
                            props.onFinalize(props.index, t);
                        }
                    }}
                    disabled={
                        !markerPos ||
                        !(cameraPos || props.noCameraPos) ||
                        !title ||
                        !title.match(/\S/) ||
                        !touched ||
                        durationError ||
                        (audioFile && audioFile.size > AUDIOFILESIZEMB * 1024 * 1024) ||
                        isSaving
                    }
                >
                    {isSaving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {props.index === -1 ? "Creating..." : "Updating..."}
                        </>
                    ) : (
                        props.index === -1 ? "Create Landmark" : "Update Landmark"
                    )}
                </Button>
            </Modal.Body>
        </Modal>
    );
};

const LandmarksMessagingModal = (props) => {
    const [statusTxt, setStatusTxt] = useState(null);
    const [isCallingAPI, setIsCallingAPI] = useState(false);
    const [currentLandmarks, setCurrentLandmarks] = useState([]);
    const [landmarksLoaded, setLandmarksLoaded] = useState(false);
    const [currentEditIndex, setCurrentEditIndex] = useState(null);
    const [showSubModal, setShowSubModal] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [showCNS, setShowCNS] = useState(false);
    const [noCameraPos, setNoCameraPos] = useState(false);
    const [nocacheUrlSuffix, _] = useState(`?now=${Date.now()}`);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [viewerStatus, setViewerStatus] = useState("loading");
    const [removeAudioLandmarks, setRemoveAudioLandmarks] = useState({});
    const [audioFileLandmarks, setAudioFileLandmarks] = useState({});
    const { userInfo } = useContext(UserInfo);
    const [language, setLanguage] = useState("");
    const [dubbingVoice, setDubbingVoice] = useState("");
    const [dubLandmarkDescriptions, setDubLandmarkDescriptions] = useState(false);
    const langOptions = Object.keys(CLOUD_TRANSLATE_LANGUAGE_MAP).map((lang) => ({
        value: lang,
        label: CLOUD_TRANSLATE_LANGUAGE_MAP[lang],
    })).sort((a, b) => a.label.localeCompare(b.label));
    const onMsgListener = (event) => {
        const data = event.data;
        if (data.description === "CurrentStatus") {
            if (data.result === "loaded") {
                setViewerStatus("loaded");
                // Request landmarks when viewer is loaded
                sendMessageToViewer(viewerId, "GetLandmarks");
            }
            return;
        }

        if (data.description === "CurrentLandmarks") {
            setCurrentLandmarks(data.result || []);
            setLandmarksLoaded(true);
            return;
        }

        if (data.description === "LandmarksAdded") {
            if (data.result === "done") {
                // Refresh landmarks list
                sendMessageToViewer(viewerId, "GetLandmarks");
            }
            return;
        }

        if (data.description === "AssetRemoved") {
            if (data.result === "done") {
                // Refresh landmarks list after removal
                sendMessageToViewer(viewerId, "GetLandmarks");
            }
            return;
        }
        if (data.description === "ComponentPropertiesUpdate") {
            return;
        }
    };

    useEffect(() => {
        window.addEventListener("message", onMsgListener);
        return () => {
            window.removeEventListener("message", onMsgListener);
        };
    }, []);

    const viewerId = "landmark-viewer" + props.vis.id;

    useEffect(() => {
        if (props.show && viewerStatus === "loaded") {
            // Request landmarks when modal opens
            sendMessageToViewer(viewerId, "GetLandmarks");
        }
    }, [props.show, viewerStatus, viewerId]);

    const updateLandmarks = useCallback((arr = null) => {
        if (isCallingAPI) {
            return;
        }
        setStatusTxt(null);
        const res = arr || currentLandmarks;

        // Use the messaging protocol to update landmarks
        if (arr) {
            setIsDirty(true);
            setCurrentLandmarks(arr);

            // Send landmarks to viewer using AddLandmarks message
            //   sendMessageToViewer(viewerId, {
            //     description: "AddLandmarks",
            //     result: res.map(landmark => ({
            //       title: landmark.title,
            //       description: landmark.description,
            //       position: landmark.position,
            //       cameraPos: landmark.cameraPos,
            //       upVector: landmark.upVector,
            //       shape: landmark.shape || "label",
            //       strokeColor: landmark.strokeColor || [1, 0, 0, 1],
            //       fillColor: landmark.fillColor || [0.5, 0.5, 0.5, 0.8],
            //       fontSize: landmark.fontSize || 20,
            //       markerScale: landmark.markerScale || 4,
            //       labelPadding: landmark.labelPadding || 15,
            //       strokeWidth: landmark.strokeWidth || 3,
            //       labelText: landmark.labelText || landmark.title,
            //       tourDuration: landmark.duration || landmark.tourDuration || 10,
            //       name: landmark.name
            //     }))
            //   });
        }
    }, [isCallingAPI, currentLandmarks, viewerId]);

    const handleClose = () => {
        if (isDirty) {
            setShowCNS(true);
        } else {
            props.onHide();
        }
    };

    const handleDeleteLandmark = async (landmark) => {
        if (landmark.name) {
            // Use RemoveAsset message to delete landmark
            sendMessageToViewer(viewerId, {
                description: "RemoveAsset",
                target: { name: landmark.name }
            });
        }
        setConfirmDelete(null);
    };

    const handleMoveLandmark = (index, direction) => {
        const arr = [...currentLandmarks];
        let [a, b] = [null, null];
        let newIndexes = [index, index];
        if (direction === "up" && index > 0) {
            [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
            newIndexes = [index - 1, index];
            [a, b] = [arr[index - 1], arr[index]];
        } else if (direction === "down" && index < arr.length - 1) {
            [arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];
            newIndexes = [index + 1, index];
            [a, b] = [arr[index + 1], arr[index]];
        }
        if (!a || !b) {
            return;
        }
        sendMessageToViewer(viewerId, {
            description: "SetComponentProperties",
            target: { name: a.name },
            result: {
                labelText: (newIndexes[0] + 1).toString()
            }
        });
        sendMessageToViewer(viewerId, {
            description: "SetComponentProperties",
            target: { name: b.name },
            result: {
                labelText: (newIndexes[1] + 1).toString()
            }
        });
        if (a.properties) {
            a.properties.labelText = (newIndexes[0] + 1).toString();
        }
        else {
            a.labelText = (newIndexes[0] + 1).toString();
        }
        if (b.properties) {
            b.properties.labelText = newIndexes[1].toString();
        }
        else {
            b.labelText = (newIndexes[1] + 1).toString();
        }
        updateLandmarks(arr);
    };

    return (
        <>
            <Modal show={props.show} onHide={handleClose} centered dialogClassName="modal-xl landmark-modal">
                <Modal.Header closeButton closeVariant="white">
                    <Modal.Title>Edit Landmarks</Modal.Title>
                </Modal.Header>
                <Modal.Body className={showCNS ? "d-none" : ""}>
                    {statusTxt && (
                        <div className={`landmark-status-message ${statusTxt.valid ? "success" : "error"}`}>
                            {statusTxt.msg}
                        </div>
                    )}
                    {currentEditIndex !== null ? (
                        <SubModal
                            show={showSubModal}
                            landmark={
                                currentEditIndex != -1 ? currentLandmarks[currentEditIndex] : {}
                            }
                            noCameraPos={noCameraPos}
                            index={currentEditIndex}
                            onHide={() => {
                                updateLandmarks();
                                setShowSubModal(false);
                                setCurrentEditIndex(null);
                            }}
                            hideFcn={() => {
                                setShowSubModal(false);
                            }}
                            showFcn={() => {
                                setShowSubModal(true);
                            }}
                            onSave={() => { }}
                            resetLandmarks={updateLandmarks}
                            canAudio={props.vis.can_audio_landmark}
                            viewerId={viewerId}
                            onFinalize={(index, newLm) => {
                                const arr = [...currentLandmarks];
                                if (index == -1) {
                                    arr.push(newLm);
                                } else {
                                    arr[index] = newLm;
                                }
                                updateLandmarks(arr);
                                setShowSubModal(false);
                                setCurrentEditIndex(null);
                                if (newLm.removeAudio) {
                                    setRemoveAudioLandmarks(prev => ({ ...prev, [newLm.name]: true }));
                                }
                                else if (newLm.audioFile) {
                                    setAudioFileLandmarks(prev => ({ ...prev, [newLm.name]: newLm.audioFile }));
                                }
                            }}
                            numLandmarks={currentLandmarks.length}
                        />
                    ) : null}
                    <div
                        style={{
                            flexGrow: 1,
                            position: "relative",
                            overflow: "hidden",
                            width: "100%",
                            minWidth: "300px",
                            minHeight: "500px",
                            height: "500px",
                            display: "flex",
                            flexDirection: "row",
                        }}
                    >
                        <iframe
                            src={props.vis.visualization_url + nocacheUrlSuffix}
                            id={viewerId}
                            className="border border-secondary rounded"
                            style={{
                                width: "75%",
                                height: "100%",
                                minHeight: "500px",
                            }}
                            onLoad={() => {
                                // Request status to check if viewer is loaded
                                setTimeout(() => { sendMessageToViewer(viewerId, "NotifyWhenLoaded"); }, 100);
                            }}
                        />
                        <div
                            className="landmarks-list-container"
                            style={{
                                width: "25%",
                                height: "100%",
                                minHeight: "500px",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <h5 className="landmarks-list-header">Landmarks</h5>
                            <div className="landmarks-scroll-container">
                                {currentLandmarks.map((t, ind) => {
                                    return (
                                        <div className="landmark-card" key={ind}>
                                            <div
                                                className="landmark-icon-btn edit-btn"
                                                aria-label="edit"
                                                role="button"
                                                tabIndex="0"
                                                title={`Edit Landmark "${(t.properties || t).title}"`}
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    setShowSubModal(true);
                                                    setCurrentEditIndex(ind);
                                                }}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        setShowSubModal(true);
                                                        setCurrentEditIndex(ind);
                                                    }
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faPencil} size="sm" />
                                            </div>

                                            <div className="landmark-card-content">
                                                <div className="landmark-card-title" dir="auto">
                                                    <span className="landmark-card-number">{ind + 1}</span>
                                                    {(t.properties || t).title}
                                                </div>
                                            </div>

                                            <div className="landmark-actions">
                                                <div className="landmark-actions-row">
                                                    {ind !== 0 && (
                                                        <div
                                                            className="landmark-icon-btn move-btn"
                                                            aria-label="move up"
                                                            role="button"
                                                            tabIndex="0"
                                                            title="Move Up"
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                handleMoveLandmark(ind, "up");
                                                            }}
                                                            onKeyDown={(event) => {
                                                                if (event.key === "Enter") {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    handleMoveLandmark(ind, "up");
                                                                }
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faArrowUp} size="sm" />
                                                        </div>
                                                    )}
                                                    {ind !== currentLandmarks.length - 1 && (
                                                        <div
                                                            className="landmark-icon-btn move-btn"
                                                            aria-label="move down"
                                                            role="button"
                                                            tabIndex="0"
                                                            title="Move Down"
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                handleMoveLandmark(ind, "down");
                                                            }}
                                                            onKeyDown={(event) => {
                                                                if (event.key === "Enter") {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    handleMoveLandmark(ind, "down");
                                                                }
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faArrowDown} size="sm" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div
                                                    className="landmark-icon-btn delete-btn"
                                                    aria-label="delete"
                                                    role="button"
                                                    tabIndex="0"
                                                    title={`Delete Landmark "${(t.properties || t).title}"`}
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        setConfirmDelete(t);
                                                    }}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter") {
                                                            event.preventDefault();
                                                            event.stopPropagation();
                                                            setConfirmDelete(t);
                                                        }
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTrashCan} size="sm" />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <Button
                                className="new-landmark-btn"
                                onClick={() => {
                                    setCurrentEditIndex(-1);
                                    setShowSubModal(true);
                                }}
                                disabled={!landmarksLoaded}
                            >
                                + New Landmark
                            </Button>
                        </div>
                    </div>
                    {userInfo?.permissions?.mtl && (
                        <Form.Group controlId={"language"} className="my-3">
                            <span className="d-flex align-items-center justify-content-center">
                                <Form.Label style={{ width: "fit-content" }} className="text-dark pe-2">Translate Landmarks</Form.Label>
                                <Form.Select disabled={isCallingAPI} value={language} onChange={(event) => {setIsDirty(true); setLanguage(event.target.value)}} className="flex-grow-1">
                                    <option value="">No Translation</option>
                                    {userInfo?.permissions?.landmark_dubbing ? (<>
                                        <optgroup label="Dubbing Support">
                                            {langOptions.filter((option) => ELEVENLABS_LANGUAGE_MAP[option.value]).map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="No Dubbing Support">
                                            {langOptions.filter((option) => !ELEVENLABS_LANGUAGE_MAP[option.value]).map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </optgroup>
                                    </>) :
                                        langOptions.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                </Form.Select>
                            </span>
                        </Form.Group>
                    )}
                    {userInfo?.permissions?.landmark_dubbing && (!language || ELEVENLABS_LANGUAGE_MAP[language]) && (
                        <Form.Group controlId={"dubbing-voice"} className="my-3">
                            <div className="landmark-dub-checkbox-container">
                                <Form.Check 
                                    type="checkbox" 
                                    label="Generate voiceover for landmarks" 
                                    checked={dubLandmarkDescriptions} 
                                    onChange={(event) => {setIsDirty(true); setDubLandmarkDescriptions(event.target.checked)}}
                                    className="landmark-dub-checkbox"
                                />
                            </div>
                            {dubLandmarkDescriptions && <VoiceSelector voice={dubbingVoice} onVoiceChange={(voice) => {setIsDirty(true); setDubbingVoice(voice)}} disabled={isCallingAPI} language={language} selectClassName="form-select" playbackRate={1.0} />}
                        </Form.Group>
                    )}
                    <Button
                        onClick={() => SubmitChanges({
                            onStart: () => { setIsCallingAPI(true); setStatusTxt(null); },
                            onSuccess: (res) => {
                                setIsCallingAPI(false);
                                setStatusTxt({ valid: true, msg: "✓ Landmarks Updated Successfully!" });
                                setIsDirty(false);
                                if (props.onVisEdit) {
                                    props.onVisEdit(res.visualization);
                                }
                                setAudioFileLandmarks({});
                                setRemoveAudioLandmarks({});
                            },
                            onError: (err) => {
                                setIsCallingAPI(false);
                                setStatusTxt({ valid: false, msg: err.message || (err.toUpperCase ? err : "Internal Server Error") });
                            },
                            audioFileLandmarks,
                            removeAudioLandmarks,
                            currentLandmarks,
                            language,
                            selectedVoice: (dubLandmarkDescriptions && (!language || ELEVENLABS_LANGUAGE_MAP[language])) ? dubbingVoice : null,
                            visualization: props.vis,
                            viewerId: viewerId,
                        })}
                        className="mt-4 landmark-commit-btn"
                        disabled={isCallingAPI || !isDirty}
                    >
                        {isCallingAPI ? (
                            <>
                                <Spinner
                                    animation="border"
                                    role="loading"
                                    className="align-center"
                                    size="sm"
                                    style={{ marginRight: "0.5rem" }}
                                />
                                Saving...
                            </>
                        ) : (
                            "Commit Changes"
                        )}
                    </Button>
                </Modal.Body>
            </Modal>

            {confirmDelete && (
                <DeleteModal
                    show={!!confirmDelete}
                    vis={confirmDelete}
                    onVisDelete={handleDeleteLandmark}
                    onHide={() => {
                        setConfirmDelete(null);
                    }}
                    isLandmark
                />
            )}

            <Modal show={showCNS} onHide={() => setShowCNS(false)} centered>
                <Modal.Header closeButton closeVariant="white">
                    <Modal.Title>Unsaved Changes</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    You have unsaved changes. Are you sure you want to exit?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCNS(false)}>
                        Stay
                    </Button>
                    <Button variant="primary" onClick={() => {
                        setShowCNS(false);
                        props.onHide();
                    }}>
                        Exit
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default LandmarksMessagingModal;
